import { logout } from "../helpers/helpers";
// import AppState from "../store/appstate";
import useAppStore from "../store/appStateZustand.ts";

// Track ongoing retry attempts to prevent cascading failures
const retryAttempts = new Set();

export async function relogin() {
  // This organization doesn't use renewal tokens - always logout on expiry
  console.log("[relogin] Organization doesn't support token renewal - logging out");
  logout();
  throw new Error("Request was not authorized.");
}

export async function processRes(res, noAuth, isRetry = false) {
  const contentType = res.headers.get("content-type");
  if (res.status === 401 && !noAuth) {
    // Check if token is actually expired or if this is a temporary server issue
    const currentAuth = useAppStore.getState().authentication;
    const currentTime = Date.now();

    // Validate authentication state - handle both null and empty object cases
    if (!currentAuth || currentAuth === null || !currentAuth.expires_at || typeof currentAuth.expires_at !== "number") {
      console.warn(`[AUTH] Invalid authentication state - logging out: ${res.url}`);
      logout();
      throw new Error("Request was not authorized.");
    }

    const timeUntilExpiry = (currentAuth.expires_at - currentTime) / 1000 / 60;

    // Only logout if token is actually expired by more than 1 minute OR this is a retry
    const bufferMs = 1 * 60 * 1000; // 1 minute buffer
    const isTokenActuallyExpired = currentTime > (currentAuth.expires_at + bufferMs);

    if (isTokenActuallyExpired || isRetry) {
      console.warn(`[AUTH] Logging out due to ${isTokenActuallyExpired ? "expired token" : "failed retry"} - ${res.url}`);
      logout();
      throw new Error("Request was not authorized.");
    }
    else {
      console.warn(`[AUTH] 401 received but token valid (${Math.round(timeUntilExpiry * 100) / 100} min remaining) - retrying: ${res.url}`);
      throw new Error("Temporary authorization error - please try again.");
    }
  }
  if ([402, 403, 404, 405, 406, 407, 408, 410, 411, 412].includes(res.status)) {
    let message = "";
    try {
      const data = await res.json();
      message = data.messages.join(", ");
    }
    catch {
      message = "A server error has occurred.";
    }
    throw new Error(message);
  }
  if (res.status === 500) {
    let traceId = null;
    try {
      const data = await res.json();
      traceId = data?.traceId || null;
    }
    catch {
      // Response body may not be JSON — that's fine
    }
    const err = new Error("A server error has occurred.");
    err.traceId = traceId;
    throw err;
  }
  if (contentType === null)
    return Promise.resolve(null);
  if (contentType.startsWith("application/json;")) {
    return res.json();
  }
  if (contentType.startsWith("text/plain;"))
    return res.text();
  throw new Error(`Unsupported response content-type: ${contentType}`);
}

export function apiCall({
  endpoint,
  method,
  data,
  path,
  clientId = false,
  noAuth = false,
  skipKycCheck = false,
}) {
  // const apiUrl = AppState.getRawState()?.apiUrl;
  // const kyc = AppState.getRawState()?.kyc;
  // const accessToken = AppState.getRawState()?.authentication?.access_token;
  const state = useAppStore.getState();
  const apiUrl = state.apiUrl;
  const kyc = state.kyc;
  const accessToken = state.authentication?.access_token;

  if (!skipKycCheck && (method === "put" || method === "post") && (kyc?.kycrequired && !kyc?.kycpassed)) {
    throw new Error("Please verify your identity to continue making changes to your account");
  }
  let append = "";
  if (path && path.length) {
    append = `?path=${encodeURIComponent(path)}`;
    if (clientId) {
      append += "&ci=1";
    }
  }
  if (clientId && !path) {
    append = "?ci=1";
  }

  const makeRequest = (isRetry = false) => {
    return fetch(`${apiUrl}${endpoint}${append}`, {
      method: method || "get",
      headers: {
        Authorization: (!noAuth && accessToken) ? `Bearer ${accessToken}` : null,
      },
      body: data ? JSON.stringify(data) : null,
    }).then((res) => {
      return processRes(res, noAuth, isRetry);
    });
  };

  // Create a unique key for this request to prevent duplicate retries
  const requestKey = `${method || "get"}:${endpoint}${append}`;

  // Try the request, and if it fails with a temporary auth error, retry once
  return makeRequest(false).catch((error) => {
    if (error.message === "Temporary authorization error - please try again.") {
      // Prevent multiple simultaneous retries of the same request
      if (retryAttempts.has(requestKey)) {
        console.warn(`[AUTH] Skipping retry for ${requestKey} - already in progress`);
        throw new Error("Request was not authorized.");
      }

      retryAttempts.add(requestKey);
      console.info("[AUTH] Retrying API request after temporary 401");

      return makeRequest(true).finally(() => {
        // Clean up retry tracking
        retryAttempts.delete(requestKey);
      });
    }
    throw error;
  });
}

export function apiCallWithFormData({
  endpoint,
  method,
  data,
  path,
  clientId = false,
  noAuth = false,
}) {
  // const apiUrl = AppState.getRawState()?.apiUrl;
  // const accessToken = AppState.getRawState()?.authentication?.access_token;
  // const kyc = AppState.getRawState()?.kyc;
  const state = useAppStore.getState();
  const apiUrl = state.apiUrl;
  const accessToken = state.authentication?.access_token;
  const kyc = state.kyc;
  if ((method === "put" || method === "post") && (kyc?.kycrequired && !kyc?.kycpassed)) {
    throw new Error("Please verify your identity to continue making changes to your account");
  }
  let append = "";
  if (path && path.length) {
    append = `?path=${encodeURIComponent(path)}`;
    if (clientId) {
      append += "&ci=1";
    }
  }
  if (clientId && !path) {
    append = "?ci=1";
  }
  return fetch(`${apiUrl}${endpoint}${append}`, {
    method: method || "get",
    headers: {
      Authorization: (!noAuth && accessToken) ? `Bearer ${accessToken}` : null,
    },
    body: data,
  })
    .then(res => processRes(res, noAuth));
}
